import { formatDistanceToNow } from "date-fns";
import {
  Bell,
  CircleUserRound,
  FileText,
  Headset,
  Hotel,
  MoonStar,
  Palmtree,
  Plane,
  ScrollText,
  UsersRound,
} from "lucide-react";

import { baseApi } from "@/redux/api/base-api";
import type { NotificationEntityType } from "../types/notification.types";

/* ─── Entity icon ─── */

export function renderNotificationEntityIcon(
  entityType: string,
  className = "size-4",
) {
  const props = { className };

  switch (normalizeNotificationEntityType(entityType)) {
    case "visa_application":
      return <ScrollText {...props} />;
    case "umrah_booking":
      return <MoonStar {...props} />;
    case "support_ticket":
      return <Headset {...props} />;
    case "custom_umrah_request":
      return <MoonStar {...props} />;
    case "flight_booking":
      return <Plane {...props} />;
    case "hotel_booking":
      return <Hotel {...props} />;
    case "holiday_booking":
      return <Palmtree {...props} />;
    case "invoice":
      return <FileText {...props} />;
    case "traveler":
      return <UsersRound {...props} />;
    case "Profile":
      return <CircleUserRound {...props} />;
    default:
      return <Bell {...props} />;
  }
}

/* ─── Relative time ─── */

export function formatRelativeTime(value: string | null) {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";
  return formatDistanceToNow(date, { addSuffix: true });
}

/* ─── Entity click config (navigation) ─── */

type NotificationEntityClickConfig = {
  toHref: (entityId: number) => string;
};

type NotificationInvalidateTagsResolver = (
  entityId: number,
) => Parameters<typeof baseApi.util.invalidateTags>[0];

function normalizeNotificationEntityType(entityType: string) {
  const trimmedEntityType = entityType.trim();
  const normalizedEntityType = trimmedEntityType.toLowerCase();

  if (normalizedEntityType === "profile") {
    return "Profile" as const;
  }

  return trimmedEntityType as NotificationEntityType | (string & {});
}

export const notificationEntityClickConfig: Partial<
  Record<NotificationEntityType, NotificationEntityClickConfig>
> = {
  visa_application: {
    toHref: (entityId) => `/dashboard/bookings/visa/${entityId}`,
  },
  umrah_booking: {
    toHref: (entityId) => `/dashboard/bookings/umrah/${entityId}`,
  },
  support_ticket: {
    toHref: (entityId) => `/dashboard/help-support/${entityId}`,
  },
  custom_umrah_request: {
    toHref: (entityId) => `/dashboard/bookings/umrah/custom/${entityId}`,
  },
  flight_booking: {
    toHref: (entityId) => `/dashboard/bookings/flight/${entityId}`,
  },
  hotel_booking: {
    toHref: (entityId) => `/dashboard/bookings/hotel/${entityId}`,
  },
  holiday_booking: {
    toHref: (entityId) => `/dashboard/bookings/holiday/${entityId}`,
  },
  invoice: {
    toHref: (entityId) => `/dashboard/invoices/${entityId}`,
  },
  traveler: {
    toHref: (entityId) => `/dashboard/travelers/${entityId}`,
  },
  Profile: {
    toHref: () => "/dashboard/my-profile",
  },
};

export const notificationEntityInvalidationConfig: Partial<
  Record<NotificationEntityType, NotificationInvalidateTagsResolver>
> = {
  visa_application: (entityId) => [
    { type: "VisaBookingList", id: "LIST" },
    { type: "VisaBooking", id: String(entityId) },
  ],
  umrah_booking: (entityId) => [
    { type: "UmrahBookingList", id: "LIST" },
    { type: "UmrahBooking", id: String(entityId) },
  ],
  support_ticket: (entityId) => [
    { type: "SupportTicketList", id: "LIST" },
    { type: "SupportTicket", id: String(entityId) },
  ],
  custom_umrah_request: (entityId) => [
    { type: "CustomUmrahRequestList", id: "LIST" },
    { type: "CustomUmrahRequest", id: String(entityId) },
  ],
  flight_booking: (entityId) => [
    { type: "FlightBookingList", id: "LIST" },
    { type: "FlightBooking", id: String(entityId) },
  ],
  hotel_booking: (entityId) => [
    { type: "HotelBookingList", id: "LIST" },
    { type: "HotelBooking", id: String(entityId) },
  ],
  holiday_booking: (entityId) => [
    { type: "HolidayBookingList", id: "LIST" },
    { type: "HolidayBooking", id: String(entityId) },
  ],
  invoice: (entityId) => [
    { type: "InvoiceList", id: "LIST" },
    { type: "Invoice", id: String(entityId) },
  ],
  traveler: (entityId) => [
    { type: "TravelerList", id: "LIST" },
    { type: "Traveler", id: String(entityId) },
  ],
  Profile: () => ["AuthProfile"],
};

export function getNotificationInvalidateTags(
  entityType: string,
  entityId: number,
) {
  const normalizedEntityType = normalizeNotificationEntityType(entityType);

  return notificationEntityInvalidationConfig[
    normalizedEntityType as NotificationEntityType
  ]?.(entityId);
}
